import { useQuery } from '@tanstack/react-query'
import { UsersRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Avatar } from '@/components/ui/Avatar'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { familyAttendanceQuery } from '@/features/attendance/api'
import { StudentAttendanceView } from '@/features/attendance/components/StudentAttendanceView'
import { familyQuery } from '@/features/portal/api'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'

export function FamilyAttendancePage() {
  const { t } = useTranslation('attendance')
  useDocumentTitle('Attendance')
  const children = useQuery(familyQuery())
  const [selected, setSelected] = useState(0)

  if (children.isPending) return <LoadingState />
  if (children.error) return <ErrorState onRetry={() => void children.refetch()} />
  const child = children.data[selected] ?? children.data[0]

  return (
    <>
      <PageHeader
        title={t('Attendance')}
        description={t('Daily attendance for the current academic year.')}
        breadcrumbs={[{ label: t('Attendance') }]}
        homeHref="/portal"
      />
      {children.data.length > 1 ? (
        <div role="tablist" aria-label={t('Choose child')} className="mb-6 flex gap-2 overflow-x-auto">
          {children.data.map((c, i) => (
            <button
              key={c.student.id}
              role="tab"
              type="button"
              aria-selected={i === selected}
              onClick={() => setSelected(i)}
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-full border py-1 pr-4 pl-1 text-sm font-medium transition-colors',
                i === selected
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-line bg-surface text-ink-soft hover:border-line-strong',
              )}
            >
              <Avatar name={c.student.fullName} src={c.student.photoUrl} size="sm" />
              {c.student.firstName}
            </button>
          ))}
        </div>
      ) : null}
      {child ? (
        <ChildAttendance studentId={child.student.id} />
      ) : (
        <Card>
          <EmptyState
            icon={<UsersRound />}
            title={t('No student linked to your account')}
            description={t("Ask the school office to link your login to your child's record.")}
          />
        </Card>
      )}
    </>
  )
}

function ChildAttendance({ studentId }: { studentId: number }) {
  const query = useQuery(familyAttendanceQuery(studentId))
  if (query.isPending) return <LoadingState />
  if (query.error) return <ErrorState onRetry={() => void query.refetch()} />
  return <StudentAttendanceView key={studentId} data={query.data} />
}
